export interface LocaleOption {
  tag: string;
  label: string;
  language: string;
  country: string;
  countryCode: string;
  aliases: string[];
}

// Keep this whitelist in sync with wedding-upload-be/functions/src/locales.ts.
export const SUPPORTED_LOCALE_TAGS = [
  "af-ZA",
  "am-ET",
  "ar-SA",
  "az-AZ",
  "be-BY",
  "bg-BG",
  "bn-BD",
  "bs-BA",
  "ca-ES",
  "cs-CZ",
  "cy-GB",
  "da-DK",
  "de-AT",
  "de-CH",
  "de-DE",
  "el-GR",
  "en-AU",
  "en-CA",
  "en-GB",
  "en-IE",
  "en-IN",
  "en-NZ",
  "en-US",
  "es-AR",
  "es-CL",
  "es-CO",
  "es-ES",
  "es-MX",
  "es-PE",
  "et-EE",
  "eu-ES",
  "fa-IR",
  "fi-FI",
  "fil-PH",
  "fr-BE",
  "fr-CA",
  "fr-CH",
  "fr-FR",
  "ga-IE",
  "gl-ES",
  "gu-IN",
  "he-IL",
  "hi-IN",
  "hr-HR",
  "hu-HU",
  "id-ID",
  "is-IS",
  "it-IT",
  "ja-JP",
  "ka-GE",
  "kk-KZ",
  "km-KH",
  "kn-IN",
  "ko-KR",
  "lo-LA",
  "lt-LT",
  "lv-LV",
  "mk-MK",
  "ml-IN",
  "mn-MN",
  "mr-IN",
  "ms-MY",
  "mt-MT",
  "nb-NO",
  "ne-NP",
  "nl-BE",
  "nl-NL",
  "pa-IN",
  "pl-PL",
  "pt-BR",
  "pt-PT",
  "ro-RO",
  "ru-RU",
  "si-LK",
  "sk-SK",
  "sl-SI",
  "sq-AL",
  "sr-RS",
  "sv-SE",
  "sw-KE",
  "ta-IN",
  "te-IN",
  "th-TH",
  "tr-TR",
  "uk-UA",
  "ur-PK",
  "uz-UZ",
  "vi-VN",
  "zh-CN",
  "zh-HK",
  "zh-TW",
] as const;

function buildLocaleLabel(tag: string): string {
  const [language, region] = tag.split("-");
  if (!language || !region) return tag;

  if (typeof Intl === "undefined" || typeof Intl.DisplayNames === "undefined") {
    return `${language.toUpperCase()} (${region.toUpperCase()})`;
  }

  const languageName = new Intl.DisplayNames(["en"], { type: "language" }).of(language);
  const regionName = new Intl.DisplayNames(["en"], { type: "region" }).of(region.toUpperCase());

  if (languageName && regionName) {
    return `${languageName} (${regionName})`;
  }
  if (languageName) {
    return languageName;
  }
  return `${language.toUpperCase()} (${region.toUpperCase()})`;
}

function buildLocaleAliases(tag: string, label: string): string[] {
  const [language = "", region = ""] = tag.split("-");
  return [
    tag,
    tag.toLowerCase(),
    language,
    language.toLowerCase(),
    region,
    region.toLowerCase(),
    label,
    label.toLowerCase(),
  ];
}

export const SUPPORTED_LOCALES: LocaleOption[] = SUPPORTED_LOCALE_TAGS.map((tag) => {
  const [language = "", region = ""] = tag.split("-");
  const label = buildLocaleLabel(tag);
  const languageName = label.split(" (")[0] ?? language.toUpperCase();
  const countryName = label.match(/\(([^)]+)\)/)?.[1] ?? region.toUpperCase();
  return {
    tag,
    label,
    language: languageName,
    country: countryName,
    countryCode: region.toUpperCase(),
    aliases: buildLocaleAliases(tag, label),
  };
});

const localeByTag = new Map(SUPPORTED_LOCALES.map((l) => [l.tag, l]));

export function getLocaleOption(tag: string): LocaleOption | null {
  return localeByTag.get(tag) ?? null;
}

export function getLocaleSearchValue(tag: string): string {
  const option = getLocaleOption(tag);
  if (!option) return tag;
  return [
    option.tag,
    option.label,
    option.language,
    option.country,
    option.countryCode,
    ...option.aliases,
  ].join(" ");
}

export function getLocaleCountryCode(tag: string): string {
  const option = getLocaleOption(tag);
  if (option) {
    return option.countryCode;
  }
  const region = tag.split("-")[1] ?? "";
  return region.toUpperCase();
}

export function formatLocaleLabel(tag: string): string {
  const option = getLocaleOption(tag);
  if (!option) return tag;
  return option.label;
}

export function formatLocaleLabelWithTag(tag: string): string {
  const option = getLocaleOption(tag);
  if (!option) return tag;
  return `${option.label} (${option.tag})`;
}
